import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import { Flame } from "lucide-react";
import api from "@/lib/api";

function HeatLayer({ points }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!points || points.length === 0) return;
    layerRef.current = L.heatLayer(points, {
      radius: 32, blur: 24, maxZoom: 17,
      gradient: { 0.2: "#fcd34d", 0.4: "#f59e0b", 0.7: "#ef4444", 1.0: "#dc2626" },
    }).addTo(map);
    // Fit bounds
    if (points.length > 1) {
      const b = L.latLngBounds(points.map((p) => [p[0], p[1]]));
      map.fitBounds(b, { padding: [40, 40] });
    } else {
      map.setView([points[0][0], points[0][1]], 13);
    }
    return () => { if (layerRef.current) map.removeLayer(layerRef.current); };
  }, [points, map]);
  return null;
}

export default function DriverHeatmap() {
  const [data, setData] = useState({ scope: "personal", points: [], ride_count: 0, max_payout: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/chauffeur/drivers/me/heatmap?days=7")
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const center = data.points[0] ? [data.points[0][0], data.points[0][1]] : [18.018, -76.81];

  return (
    <div data-testid="driver-heatmap" className="border border-gold/20 rounded-md overflow-hidden bg-luxury-black/50">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/80 flex items-center gap-1"><Flame size={10} /> Earnings Heatmap</p>
          <p className="font-heading text-base text-white">{data.scope === "personal" ? "Where you earned" : "Where the platform pays best"}</p>
        </div>
        <div className="text-right">
          <p className="text-white text-xs font-body">{data.ride_count} rides · 7d</p>
          <p className="text-gold/70 text-[10px] font-body">peak zone JMD {data.max_payout?.toLocaleString() || 0}</p>
        </div>
      </div>
      <div style={{ height: 320 }}>
        {loading ? (
          <div className="flex items-center justify-center h-full text-white/40 font-body text-xs">Loading…</div>
        ) : data.points.length === 0 ? (
          <div className="flex items-center justify-center h-full text-white/40 font-body text-xs">No completed rides yet — go online to start earning</div>
        ) : (
          <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%", background: "#0a0a0a" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap' />
            <HeatLayer points={data.points} />
          </MapContainer>
        )}
      </div>
    </div>
  );
}
